import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, RouteProps, Router, Switch } from 'react-router-dom';
import { AppDispatch } from 'store/store';
import { MainLayout } from 'modules/core/layout/MainLayout';
import { ErrorPage } from 'pages/ErrorPage/ErrorPage';
import { getDisabledFeaturesList, getFetchingUIFlag } from 'store/selectors/ui.selector';
import { getAuthState, getUser } from 'store/selectors/auth.selector';
import { getFeaturesListThunk } from 'actions/featuresListActions';
import { TabContent, Loader } from 'modules/core/components';
import { sendMetric } from 'metrics';
import { getPathnameWithoutLastElement } from 'modules/Learning/Learning.utils';
import { getLendersListThunk } from 'actions/lenderAction';
import { getCurrentLenderId, getFetchingLendersFlag, getLoadedLenderFlag } from 'store/selectors/lenders.selector';
import { getLenderState, getLendersList } from 'store/selectors/lenders.selector';
import {
  getDealerSsoLoginModalLanding,
  getDealerSsoSelectedLenderPath,
  isDealerSsoLenderSelectionComplete,
} from 'modules/auth/utils/dealerSsoLenderSelection.utils';
import { DEALER_SSO_LENDER_SELECTED_EVENT } from 'modules/auth/constants/auth.const';
import { useDebounceForLoading } from 'router/utils/useDebounceForLoading';
import { SUPPORTED_FEATURES_MAPPER } from 'modules/core/constants/mappers/supportedFeatures.mapper';
import { connectWithAlternativeSsoThunk } from 'actions/authActions';
import { checkUserThunk } from 'actions/userActions';
import { getDefaultVehiclePhotoThunk, fetchDealSourceDataThunk } from 'actions/vehicleActions';
import { getPassengerCapacitiesThunk } from 'actions/passengerCapacitiesAction';
import { ProductFruits } from 'react-product-fruits';
import { ADMIN_URL_PREFIX, LOGIN_URL_PREFIX } from 'modules/core/constants';
import { LENDERS } from 'modules/core/components/TabContent/common.const';
import { IMenuLink } from 'modules/core/components/SideBar/sideBarMenuLinks';
import { getRACInitialValues } from 'store/selectors/RACAffordabilityEngine.selector';
import { SSO_TYPE } from 'modules/core/constants';
import { DealSourceProvider } from 'modules/core/enums';
import { useLenderMode } from 'modules/Lender/hooks/useLenderMode';

import { RouterItem } from './RouterItem';
import { RouteOneMaintenanceGate } from './RouteOneMaintenanceGate';
import { ROUTER_PATH } from './const/routerPath.const';
import {
  AUTH_ROUTES,
  LEARNING_ROUTES,
  MAKE_IT_WORKS_WITHOUT_INVENTORY_ROUTES,
  PRIVATE_ROUTES,
} from './const/routerConfig.const';
import { browserHistory } from './browserHistory';
import { NavigationService } from './navigation.service';
import { useCheckAuthUser } from '../hooks';
import { IRoute } from './types';
import { getLoginLink } from './utils/getRedirectLink';
import { getAlternativeSsoFormValues, isAlternativeSsoLink } from './utils/alternativeSso';
import { useDisabledFeatureRedirect } from './hooks';

const PRODUCT_FRUITS_WORKSPACE_CODE = process.env.REACT_APP_PRODUCT_FRUITS_WORKSPACE_CODE;

export const Navigation: FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuth, isFetching: isAuthFetching, isAlternativeSsoFetching } = useSelector(getAuthState);
  const isFetchingFeatureList = useSelector(getFetchingUIFlag);
  const user = useSelector(getUser);
  const isFetchingLendersList = useSelector(getFetchingLendersFlag);
  const currentLenderId = useSelector(getCurrentLenderId);
  const lenderState = useSelector(getLenderState);
  const lenders = useSelector(getLendersList);
  const isLoginSubdomain = window.location.hostname.includes(LOGIN_URL_PREFIX);
  const isSelectDealerPage = window.location.pathname.includes(ROUTER_PATH.SELECT_DEALER);
  const isAuthenticationFetching = isLoginSubdomain ? !isLoginSubdomain : isAuthFetching;
  const racInitialValues = useSelector(getRACInitialValues);
  const isDealerSSO = user?.sso_type === SSO_TYPE.DEALER;
  const isLoadedLender = useSelector(getLoadedLenderFlag);
  const lastCalledLenderIdRef = useRef<string | null>(null);
  const { lenderHasRouteOneConfig } = useLenderMode(user, lenderState, lenderState?.dealSourceData);

  const getIsFetching = useCallback(() => {
    const isAuthAndNotAdminUrl = isAuth && !window.location.hostname.includes(ADMIN_URL_PREFIX);

    if (isAuthAndNotAdminUrl && isSelectDealerPage) {
      return false;
    }

    if (isAuthAndNotAdminUrl && !isLoginSubdomain) {
      return isAuthFetching || isFetchingFeatureList || isFetchingLendersList;
    } else return isAuthenticationFetching;
  }, [
    isAuth,
    isAuthFetching,
    isAuthenticationFetching,
    isFetchingFeatureList,
    isFetchingLendersList,
    isLoginSubdomain,
    isSelectDealerPage,
  ]);

  const isFetching = getIsFetching();
  const isLoading = useDebounceForLoading(isFetching);

  const disabledFeaturesList = useSelector(getDisabledFeaturesList);
  const { createRedirectRoute } = useDisabledFeatureRedirect();

  const dealerSsoLoginLanding = useMemo(() => getDealerSsoLoginModalLanding(user, lenders, disabledFeaturesList), [
    user,
    lenders,
    disabledFeaturesList,
  ]);

  const [dealerSsoLenderSelectionTick, setDealerSsoLenderSelectionTick] = useState(0);

  useEffect(() => {
    const handleDealerSsoLenderSelected = () => setDealerSsoLenderSelectionTick((tick) => tick + 1);

    window.addEventListener(DEALER_SSO_LENDER_SELECTED_EVENT, handleDealerSsoLenderSelected);

    return () => window.removeEventListener(DEALER_SSO_LENDER_SELECTED_EVENT, handleDealerSsoLenderSelected);
  }, []);

  const redirectFreeLenders = useMemo(() => lenders.filter((lender) => lender.withoutLenderRedirect), [lenders]);

  const isDealerSsoLenderSelected = useMemo(
    () => isDealerSsoLenderSelectionComplete(user, redirectFreeLenders, disabledFeaturesList),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, redirectFreeLenders, disabledFeaturesList, dealerSsoLenderSelectionTick],
  );

  const userInfo = {
    username: user?.email || 'Anonymous',
    props: {
      dealSource: user?.dealSource || '',
    },
  };

  const productFruitsConfig = {
    disableBannersAutoMargin: true,
  };

  const enabledPrivateRoutes = NavigationService.filterByDisabledFeatures(PRIVATE_ROUTES, disabledFeaturesList);

  const renderComponent = useCallback(
    (Component?: FC) => (props: RouteProps) => (Component ? <Component {...props} /> : null),
    [],
  );

  const mainLayoutWrapperCallback = useCallback(
    (isSinglePage: boolean, component?: FC) => (props: RouteProps) => (
      <MainLayout isSinglePage={isSinglePage}>{renderComponent(component)(props)}</MainLayout>
    ),
    [renderComponent],
  );

  const renderRoutes = useCallback(
    (
      routes: IRoute[],
      defaultExact,
      isPrivate = true,
      isAccessibleToAll = false,
      wrapperCallback = null,
      isSinglePage = false,
    ) =>
      routes.map(({ path, component, exact = defaultExact }: IRoute) => (
        <RouterItem
          render={wrapperCallback ? wrapperCallback(isSinglePage, component) : renderComponent(component)}
          path={path}
          key={path}
          exact={exact}
          isPrivate={isPrivate}
          isAccessibleToAll={isAccessibleToAll}
        />
      )),
    [renderComponent],
  );

  const renderProbabilityEstimatorRedirectRoute = useCallback(
    () =>
      createRedirectRoute({
        featureName: SUPPORTED_FEATURES_MAPPER.probability_estimator,
        routePath: ROUTER_PATH.PROBABILITY_ESTIMATOR,
        lenderFilterFn: (lender: IMenuLink) => !!lender.isShopByBudgetEnabled,
      })(),
    [createRedirectRoute],
  );

  const renderApprovedLoanInputRedirectRoute = useCallback(
    () =>
      createRedirectRoute({
        featureName: SUPPORTED_FEATURES_MAPPER.approved_loan_input,
        routePath: ROUTER_PATH.APPROVED_LOAN_INPUT,
        lenderFilterFn: (lender: IMenuLink) => !!lender.isShopByCallbackEnabled,
      })(),
    [createRedirectRoute],
  );

  useEffect(() => {
    if (
      isAuth &&
      !window.location.hostname.includes(ADMIN_URL_PREFIX) &&
      !window.location.hostname.includes(LOGIN_URL_PREFIX)
    ) {
      dispatch(getPassengerCapacitiesThunk());
      dispatch(getFeaturesListThunk());
      dispatch(getLendersListThunk());
      dispatch(getDefaultVehiclePhotoThunk());
    }
  }, [isAuth, dispatch, user?.isWithInventory]);

  useEffect(() => {
    if (isAuth && isDealerSSO && isLoginSubdomain && !user?.tenants) {
      dispatch(getFeaturesListThunk(user?.tenant?.subdomain));
      dispatch(getLendersListThunk(user?.tenant?.subdomain));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth, isDealerSSO, isLoginSubdomain, user?.tenant?.subdomain, user?.tenants]);

  useCheckAuthUser();

  const [pathname, setPathname] = useState(browserHistory.location.pathname);

  useEffect(() => {
    const unlisten = browserHistory.listen((location) => {
      setPathname(location.pathname);
    });

    return () => unlisten();
  }, []);

  const isRacRoute = pathname === ROUTER_PATH.RAC_AFFORDABILITY_ENGINE;

  useEffect(() => {
    if (
      isAuth &&
      user?.is_sso &&
      !window.location.hostname.includes(ADMIN_URL_PREFIX) &&
      !window.location.hostname.includes(LOGIN_URL_PREFIX) &&
      (!window.location.pathname.includes(LENDERS) || currentLenderId)
    ) {
      if (isDealerSSO) return;

      const initialValues = isRacRoute ? racInitialValues : lenderState?.initialValues;

      dispatch(
        fetchDealSourceDataThunk({
          deal_source: user?.dealSource ?? DealSourceProvider.ROUTE_ONE,
          conversation_id: NavigationService.getDealSourceConversationId(user, initialValues?.routeOneSearch),
          dealer_id:
            initialValues?.routeOneDealer?.value != null
              ? String(initialValues.routeOneDealer.value)
              : user?.originalExternalDealerId,
          lender_id: isRacRoute ? undefined : lenderState?.id,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.is_sso,
    user?.dealSource,
    isDealerSSO,
    isAuth,
    dispatch,
    currentLenderId,
    lenderState?.id,
    isRacRoute,
    user?.originalConversationId,
    user?.originalExternalDealerId,
  ]);

  useEffect(() => {
    if (
      !isAuth ||
      !isDealerSSO ||
      window.location.hostname.includes(ADMIN_URL_PREFIX) ||
      window.location.hostname.includes(LOGIN_URL_PREFIX) ||
      !window.location.pathname.includes(LENDERS) ||
      !currentLenderId ||
      !isLoadedLender
    )
      return;

    if (!isDealerSsoLenderSelected) return;

    const selectedLenderFullId = getDealerSsoSelectedLenderPath()?.split('/')[2] ?? null;

    if (selectedLenderFullId && selectedLenderFullId !== currentLenderId) return;

    const isSBBorSBCEnabled = !!lenderState?.isShopByBudgetEnabled || !!lenderState?.isShopByCallbackEnabled;

    if (!lenderHasRouteOneConfig && !isSBBorSBCEnabled) return;

    if (lastCalledLenderIdRef.current === currentLenderId) return;

    lastCalledLenderIdRef.current = currentLenderId;

    dispatch(
      fetchDealSourceDataThunk({
        deal_source: user?.dealSource ?? DealSourceProvider.ROUTE_ONE,
        conversation_id: NavigationService.getDealSourceConversationId(
          user,
          lenderState?.initialValues?.routeOneSearch,
        ),
        dealer_id:
          lenderState?.initialValues?.routeOneDealer?.value != null
            ? String(lenderState?.initialValues.routeOneDealer.value)
            : user?.originalExternalDealerId,
        lender_id: lenderState?.id,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuth,
    isDealerSSO,
    isLoadedLender,
    currentLenderId,
    lenderState?.id,
    dispatch,
    user?.dealSource,
    user?.originalConversationId,
    user?.originalExternalDealerId,
    isDealerSsoLenderSelected,
  ]);

  useEffect(() => {
    const isAlternativeSsoLoading = isAlternativeSsoLink() && isAlternativeSsoFetching;

    if (isAuth && !isAlternativeSsoLoading && window.location.hostname.includes(LOGIN_URL_PREFIX) && user?.is_sso) {
      if (isDealerSSO && (isFetchingLendersList || dealerSsoLoginLanding.landingPath)) {
        return;
      }

      dispatch(
        fetchDealSourceDataThunk({
          deal_source: user?.dealSource ?? DealSourceProvider.ROUTE_ONE,
          conversation_id: NavigationService.getDealSourceConversationId(
            user,
            lenderState?.initialValues?.routeOneSearch,
          ),
          dealer_id:
            lenderState?.initialValues?.routeOneDealer?.value != null
              ? String(lenderState?.initialValues.routeOneDealer.value)
              : user?.originalExternalDealerId,
          lender_id: lenderState?.id,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.is_sso,
    user?.dealSource,
    isAuth,
    dispatch,
    isAlternativeSsoFetching,
    lenderState?.id,
    user?.originalConversationId,
    user?.originalExternalDealerId,
    isDealerSSO,
    isFetchingLendersList,
    dealerSsoLoginLanding.landingPath,
  ]);

  useEffect(() => {
    const connectWithAlternativeSso = async () => {
      const values = getAlternativeSsoFormValues();

      await dispatch(connectWithAlternativeSsoThunk(values));
      await dispatch(checkUserThunk({ history: browserHistory }));
    };

    if (isAuth && user && isAlternativeSsoLink()) {
      connectWithAlternativeSso();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isAuth, Object.keys(user || {}).length]);

  useEffect(() => {
    if (getPathnameWithoutLastElement() === ROUTER_PATH.LEARNING.START) {
      sendMetric(`Completed 1/${LEARNING_ROUTES.length} screens`);
    }
  }, []);

  return isLoading ? (
    <Loader />
  ) : (
    <>
      {PRODUCT_FRUITS_WORKSPACE_CODE && (
        <ProductFruits
          config={productFruitsConfig}
          user={userInfo}
          workspaceCode={PRODUCT_FRUITS_WORKSPACE_CODE}
          language="en"
        />
      )}
      <Router history={browserHistory}>
        <RouteOneMaintenanceGate>
          <TabContent />
          <Switch>
            <Route exact path={ROUTER_PATH.BASE} render={() => <Redirect to={getLoginLink()} />} />
            {renderRoutes(AUTH_ROUTES, false, false)}
            {user?.isWithInventory && renderRoutes(LEARNING_ROUTES, false, false, true)}
            {renderProbabilityEstimatorRedirectRoute()}
            {renderApprovedLoanInputRedirectRoute()}
            {user?.isWithInventory && renderRoutes(enabledPrivateRoutes, true, true, false, mainLayoutWrapperCallback)}
            {user?.is_sso &&
              !user?.isWithInventory &&
              renderRoutes(MAKE_IT_WORKS_WITHOUT_INVENTORY_ROUTES, true, true, false, mainLayoutWrapperCallback)}
            <RouterItem path="*" component={ErrorPage} isPrivate />
          </Switch>
        </RouteOneMaintenanceGate>
      </Router>
    </>
  );
};
